import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getPropertyNotes,
  createPropertyNote,
  deletePropertyNote,
} from "@services/requests/housien-services/PropertyNotesService";
import type { PropertyNote } from "@services/requests/housien-services/PropertyNotesService";

interface UsePropertyNotesParams {
  agencyId: string;
  propertyId: string;
}

interface UsePropertyNotesResult {
  notes: PropertyNote[];
  isLoading: boolean;
  isError: boolean;
  addNote: (content: string) => Promise<boolean>;
  deleteNote: (noteId: string) => Promise<boolean>;
  refetch: () => void;
}

/**
 * Fetches property notes and syncs loading/error/data state.
 * @returns A promise that resolves once the fetch settles.
 */
async function executeFetchNotes(
  agencyId: string, propertyId: string,
  cancelRef: React.MutableRefObject<{ cancel: () => void } | null>,
  setNotes: (n: PropertyNote[]) => void, setIsError: (v: boolean) => void, setIsLoading: (v: boolean) => void,
) {
  cancelRef.current?.cancel();
  setIsLoading(true);
  setIsError(false);
  const req = getPropertyNotes(agencyId, propertyId);
  cancelRef.current = req;
  try {
    const data = await req;
    setNotes(Array.isArray(data) ? data : []);
  } catch {
    setIsError(true);
    setNotes([]);
  } finally {
    setIsLoading(false);
  }
}

/**
 * Attempts to create a note and prepend it to the list.
 * @returns True when creation succeeds, false otherwise.
 */
async function tryCreateNote(
  agencyId: string, propertyId: string, content: string,
  setNotes: React.Dispatch<React.SetStateAction<PropertyNote[]>>,
): Promise<boolean> {
  try {
    const created = await createPropertyNote(agencyId, propertyId, content);
    setNotes((prev) => [created, ...prev]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Trims the content and creates a note when non-empty.
 * @returns True when a note was created, false when blank or on failure.
 */
function createTrimmedNote(
  agencyId: string, propertyId: string, content: string,
  setNotes: React.Dispatch<React.SetStateAction<PropertyNote[]>>,
): Promise<boolean> {
  const trimmed = content.trim();
  if (!trimmed) return Promise.resolve(false);
  return tryCreateNote(agencyId, propertyId, trimmed, setNotes);
}

/**
 * Attempts to delete a note and remove it from the list.
 * @returns True when deletion succeeds, false otherwise.
 */
async function tryDeleteNote(
  agencyId: string, propertyId: string, noteId: string,
  setNotes: React.Dispatch<React.SetStateAction<PropertyNote[]>>,
): Promise<boolean> {
  try {
    await deletePropertyNote(agencyId, propertyId, noteId);
    setNotes((prev) => prev.filter((n) => n.note_id !== noteId));
    return true;
  } catch {
    return false;
  }
}

/**
 * Checks that both agency and property identifiers are present.
 * @returns True when both identifiers are non-empty.
 */
function hasIds(agencyId: string, propertyId: string): boolean {
  return Boolean(agencyId) && Boolean(propertyId);
}

/**
 * Manages property notes state, fetching, creation, and deletion.
 * @returns The notes list, status flags, and mutation callbacks.
 */
export function usePropertyNotes({ agencyId, propertyId }: UsePropertyNotesParams): UsePropertyNotesResult {
  const shouldFetch = hasIds(agencyId, propertyId);
  const [notes, setNotes] = useState<PropertyNote[]>([]);
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [isError, setIsError] = useState(false);
  const cancelRef = useRef<{ cancel: () => void } | null>(null);

  const fetchNotes = useCallback(async () => {
    if (!hasIds(agencyId, propertyId)) return;
    await executeFetchNotes(agencyId, propertyId, cancelRef, setNotes, setIsError, setIsLoading);
  }, [agencyId, propertyId]);

  useEffect(() => {
    void fetchNotes();
    return () => cancelRef.current?.cancel();
  }, [fetchNotes]);

  const addNote = useCallback(
    (content: string) => createTrimmedNote(agencyId, propertyId, content, setNotes),
    [agencyId, propertyId],
  );

  const deleteNote = useCallback(
    (noteId: string) => tryDeleteNote(agencyId, propertyId, noteId, setNotes),
    [agencyId, propertyId],
  );

  return { notes, isLoading, isError, addNote, deleteNote, refetch: fetchNotes };
}
